import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import Offcanvas from 'react-bootstrap/Offcanvas';
import Button from '../../../../components/Shared/Button';
import { categoryOptions } from '../../constants/accounts/categoryOptions';
import { columns } from '../accountsTableConfig';
import { getFilterOptions } from './accountFilters';
import type { AccountsFilterProps, AccountsFilterValues } from './AccountsFilterTypes';
import FilterDropdown from './FilterDropdown';

export type { AccountsFilterValues } from './AccountsFilterTypes';

const filterColumns = columns.filter((column) => column.key !== 'companyName' && column.key !== 'confirmedBrands');
const defaultFilterValues = Object.fromEntries(filterColumns.map((column) => [column.key, []]));
const getVisibleFilterValues = (values?: AccountsFilterValues) =>
  Object.fromEntries(
    filterColumns.map((column) => {
      const value = values?.[column.key] ?? [];
      const selectedValues = (Array.isArray(value) ? value : [value]).filter(Boolean);
      return [
        column.key,
        column.key === 'category' ? selectedValues.filter((item) => categoryOptions.includes(item)) : selectedValues,
      ];
    }),
  );

const AccountsFilter = ({ records, appliedFilters, show, onClose, onSubmit, onClear }: AccountsFilterProps) => {
  const { control, reset, handleSubmit } = useForm<AccountsFilterValues>({
    defaultValues: defaultFilterValues,
  });

  useEffect(() => {
    if (appliedFilters) reset(getVisibleFilterValues(appliedFilters));
  }, [appliedFilters, reset]);

  return (
    <Offcanvas
      show={show}
      onHide={onClose}
      placement="end"
      aria-labelledby="accounts-filters-title"
      style={{ maxWidth: '100vw' }}
    >
      <Offcanvas.Header closeButton>
        <Offcanvas.Title id="accounts-filters-title">Filters</Offcanvas.Title>
      </Offcanvas.Header>
      <Offcanvas.Body>
        <form
          onSubmit={handleSubmit((values) => {
            onSubmit?.(getVisibleFilterValues(values));
            onClose();
          })}
        >
          {filterColumns.map((field) => {
            const options = getFilterOptions(records, field.key);
            const appliedValue = appliedFilters?.[field.key];
            const appliedValues = Array.isArray(appliedValue) ? appliedValue : [appliedValue];
            if (field.key !== 'category') {
              appliedValues.forEach((value) => {
                if (value && !options.includes(value)) options.push(value);
              });
            }

            return (
              <Controller
                key={field.key}
                name={field.key}
                control={control}
                render={({ field: input }) => (
                  <FilterDropdown
                    name={field.key}
                    label={field.label}
                    options={options}
                    value={Array.isArray(input.value) ? input.value : []}
                    onChange={input.onChange}
                  />
                )}
              />
            );
          })}
          <div className="d-flex flex-wrap gap-2 mt-4">
            <Button
              className="Button Button--whiteBlack"
              onClick={() => {
                reset(defaultFilterValues);
                onClear?.();
              }}
            >
              Clear
            </Button>
            <Button className="Button" type="submit">
              Submit filters
            </Button>
          </div>
        </form>
      </Offcanvas.Body>
    </Offcanvas>
  );
};

export default AccountsFilter;
